/**
 * Export utilities for Rate Plans (Derived Rates)
 * Exports all rate plans to Excel format (.xlsx)
 */

// @e965/xlsx republishes the official SheetJS builds to npm (the public
// `xlsx` package is abandoned at 0.18.5 with unpatched CVEs).
import * as XLSX from "@e965/xlsx";
import { DISTRIBUTION_LABELS } from "./constants";

/**
 * Formats a row's data for export
 * @param {Object} row - The row data
 * @returns {Object} Formatted row data for export
 */
const formatRowForExport = (row) => {
  // Format exclusive buyers
  const exclusiveBuyers =
    row.exclusiveClients && row.exclusiveClients.length > 0
      ? row.exclusiveClients.map((c) => c.clientName || c.clientId).join(", ")
      : "-";

  return {
    "Rate Code": row.code || "-",
    "Rate Plan Name": row.name || "-",
    Status: row.isActive ? "Active" : "Inactive",
    "Exclusive Buyers": exclusiveBuyers,
    "% Applied": row.pricing || "-",
    "Derived From": row.derivedFromLabel || "-",
    // Same display labels as the on-screen badge ("Community", not "COMMUNITY");
    // unknown or missing values export as "-", matching the view.
    "Distribution Type": DISTRIBUTION_LABELS[row.distributionType] || "-",
    "Cancellation Policy": row.cancellationLabel || "-",
  };
};

/**
 * Exports ALL rate plans to Excel (.xlsx format)
 * @param {Array} allRows - All rows (unfiltered flatTree)
 * @param {string} propertyName - Property name for filename
 */
export const exportRatePlansToExcel = (allRows, propertyName = "Property") => {
  if (!allRows || allRows.length === 0) {
    throw new Error("No data to export");
  }

  // Format all rows for export (flattened structure)
  const formattedData = allRows.map(formatRowForExport);

  // Create worksheet from data
  const worksheet = XLSX.utils.json_to_sheet(formattedData);

  // Set column widths for better readability
  const columnWidths = [
    { wch: 15 }, // Rate Code
    { wch: 30 }, // Rate Plan Name
    { wch: 10 }, // Status
    { wch: 30 }, // Exclusive Buyers
    { wch: 12 }, // % Applied
    { wch: 20 }, // Derived From
    { wch: 18 }, // Distribution Type
    { wch: 20 }, // Cancellation Policy
  ];
  worksheet["!cols"] = columnWidths;

  // Create workbook and add the worksheet
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Rate Plans");

  // Generate filename with timestamp
  const timestamp = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
  const safePropertyName = propertyName.replace(/[^a-z0-9]/gi, "_");
  const filename = `Rate_Plans_${safePropertyName}_${timestamp}.xlsx`;

  // Trigger download
  XLSX.writeFile(workbook, filename);
};
